import { PlusIcon } from "@heroicons/react/solid";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  indexTutorSchedules,
  indexTutorSchedulesDateRange,
} from "../../../api/endpoints/tutors/schedules";
import { SlotStates } from "../../../api/models/ScheduleSlotModel";
import AppFilters from "../../../components/app-filters/AppFilters";
import AppButton from "../../../components/buttons/AppButton";
import ScheduleCalendar from "../../../components/calendar/ScheduleCalendar";
import ScheduleCalendarActionsHeader from "../../../components/calendar/ScheduleCalendarActionsHeader";
import InstantSessionModal from "../../../components/modal/views/InstantSessionModal";
import ShowIf from "../../../components/show-if/ShowIf";
import { useAvailabilitySchedule } from "../../../hooks/useAvailabilitySchedule";
import useCalendarViewDate from "../../../hooks/useCalendarViewDate";
import { useInfiniteTutorStudents } from "../../../hooks/useInfiniteTutorStudents";
import CalendarScheduleModel from "../../../models/CalendarScheduleModel";
import { indexTutorSessions } from "../../../api/endpoints/tutors/sessions";
import moment from "moment";

export default function WeeklySchedule() {
  const LocalBasePath = "page.tutor.dashboard";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const [showInstantSessionModal, setShowInstantSessionModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [recitationSchedule, setSchedule] = useState<object>({});
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentStudentsPage: 1,
  });

  const [availabilitySchedule, refetchAvailabilitySchedule] =
    useAvailabilitySchedule();

  const { students, loadMoreStudents } = useInfiniteTutorStudents(true);

  const getUserSchedule: any = async () => {
    const startOfTheWeek = moment(viewingDate).day(0);
    // The following will generate the 7 days of the week that the viewing date falls in.
    const dates = [...Array(7)].map((_, i) =>
      startOfTheWeek.clone().add(i, "day")
    );
    const UserScheduleApi = await indexTutorSchedulesDateRange({
      filters: {
        ...filters,
        from_date: dates[0].format("DD-MM-YYYY"),
        to_date: dates[dates.length - 1].format("DD-MM-YYYY"),
      },
    });
    if (UserScheduleApi.isSuccess) {
      return UserScheduleApi.data;
    }
  };
  const getStudentLastSession: any = async (studentId: number) => {
    const UserSessionsApi = await indexTutorSessions({
      filters: {
        student_id: studentId,
      },
      orderBy: "created_at",
      pagination: { pageNumber: 1, pageSize: 1 },
    });
    if (UserSessionsApi.isSuccess) {
      return UserSessionsApi.data;
    }
  };
  useEffect(() => {
    fetchAndSetSchedule();
  }, []);

  useEffect(() => {
    fetchAndSetSchedule();
  }, [filters, viewingDate]);

  const fetchAndSetSchedule = async () => {
    let fetchedSchedule = await getUserSchedule();
    setSchedule(fetchedSchedule);
  };

  const mergeSchedules = () => {
    const calendarAvailabilitySchedule = new CalendarScheduleModel(
      availabilitySchedule,
      SlotStates.available
    );

    let calendarRecitationSchedule = new CalendarScheduleModel(
      recitationSchedule
    );

    calendarRecitationSchedule.slots = calendarRecitationSchedule.slots.filter(
      (slot) => !slot.isCandidateSlot
    );
    calendarRecitationSchedule.modifySlotsStateByDate(viewingDate);

    /**
     * The order of spreading schedules is important as we may have duplication or different slots with the same start and end time
     * The last added slots will override the first slots in the UI if there is duplication
     * So, we give here the higher priority to the recitation schedule
     */
    let calendarSchedule = new CalendarScheduleModel({});
    calendarSchedule.slots = [
      ...calendarAvailabilitySchedule.slots,
      ...calendarRecitationSchedule.slots,
    ];
    return calendarSchedule;
  };

  const studentsOptions = students.map((student) => ({
    key: student.name,
    value: `${student.id}`,
  }));

  const onCancelFilters = () => {
    setShowFilters(false);
    setFilters({});
  };

  return (
    <div className="w-full flex flex-col">
      <div className="flex gap-4 p-6 items-center">
        <h2 className="text-2xl font-semibold text-gray-900 w-full">
          {t("schedule.weeklySchedule")}
        </h2>
        <AppButton
          buttonShape="primary"
          onClick={() => setShowInstantSessionModal(true)}
          className="gap-0.5 flex-shrink-0"
        >
          <PlusIcon className="w-6 h-6" />
          <span className="text-sm leading-[17.5px] font-normal hidden sm:inline">
            {t("instantSession.createInstantSession")}
          </span>
        </AppButton>
      </div>

      <ScheduleCalendarActionsHeader
        selectedDate={viewingDate}
        viewingDate={viewingDate}
        onChangeViewingDate={onChangeViewingDate}
        setShowFilters={setShowFilters}
        filterComponent={
          <ShowIf condition={showFilters}>
            {/**
             * you can add (loadingBottomMargin:number) if you need to load data before scrollbar have specific space to end
             */}

            <AppFilters
              className="pb-1 pt-5 flex gap-4"
              filters={filters}
              setFilters={setFilters}
              onLoadMore={(key: string) => loadMoreStudents()}
              fields={[
                {
                  name: "student_id",
                  label: t("schedule.reader"),
                  options: studentsOptions,
                  paginationLabel: "currentStudentsPage",
                },
              ]}
              handleButtonClick={onCancelFilters}
            />
          </ShowIf>
        }
      />
      <div className="h-full overflow-auto">
        <ScheduleCalendar
          selectedDate={viewingDate}
          viewingDate={viewingDate}
          schedule={mergeSchedules()}
          onChangeViewingDate={onChangeViewingDate}
          compact={true}
          cancel={true}
        />
      </div>
      <InstantSessionModal
        isOpen={showInstantSessionModal}
        onClose={() => setShowInstantSessionModal(false)}
        getStudentLastSchedule={getStudentLastSession}
      />
    </div>
  );
}
